import { normalizeValue } from "./normalizeValue"

const toStringArray = (value) => {
  if (!value) return []
  if (Array.isArray(value)) {
    return value
      .map((entry) => {
        if (typeof entry === "string") return entry.trim()
        if (
          entry &&
          typeof entry === "object" &&
          typeof entry.value === "string"
        ) {
          return entry.value.trim()
        }
        return null
      })
      .filter(Boolean)
  }
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (!trimmed) return []
    return trimmed.split(",").map((segment) => segment.trim()).filter(Boolean)
  }
  return []
}

export const normalizeUserAccessIdentifier = (value) => {
  if (typeof value === "string") {
    const trimmed = value.trim()
    if (!trimmed) return null
    const lower = trimmed.toLowerCase()
    return lower
  }
  if (
    value &&
    typeof value === "object" &&
    typeof value.emailAddress === "string"
  ) {
    return value.emailAddress.trim().toLowerCase() || null
  }
  return null
}

const stableStringify = (value) => {
  try {
    return JSON.stringify(normalizeValue(value))
  } catch (err) {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return String(value)
    }
  }
}

const extractPrincipalType = (entry) => {
  const userLink = entry?.userLink
  if (userLink && typeof userLink === "object") {
    const user = userLink.user
    if (user && typeof user === "object") {
      if (typeof user.userType === "string" && user.userType) {
        return user.userType
      }
      if (typeof user.type === "string" && user.type) {
        return user.type
      }
    }
  }
  if (typeof entry?.principalType === "string" && entry.principalType) {
    return entry.principalType
  }
  if (typeof entry?.userType === "string" && entry.userType) {
    return entry.userType
  }
  return null
}

const resolveIdentifierCandidates = (entry) => {
  const candidates = []
  if (!entry || typeof entry !== "object") return candidates
  const pushCandidate = (candidate) => {
    if (typeof candidate === "string") {
      const trimmed = candidate.trim()
      if (trimmed) candidates.push(trimmed)
    }
  }

  const pushWithDelimiters = (value) => {
    if (typeof value !== "string") return
    pushCandidate(value)
    const colonIndex = value.lastIndexOf(":")
    if (colonIndex >= 0 && colonIndex < value.length - 1) {
      pushCandidate(value.slice(colonIndex + 1))
    }
    const slashIndex = value.lastIndexOf("/")
    if (slashIndex >= 0 && slashIndex < value.length - 1) {
      pushCandidate(value.slice(slashIndex + 1))
    }
  }

  pushCandidate(entry.identifier)
  pushCandidate(entry.emailAddress)
  pushCandidate(entry.email)
  pushWithDelimiters(entry.user)
  pushWithDelimiters(entry.group)
  pushWithDelimiters(entry.serviceAccount)
  pushWithDelimiters(entry.subject)
  pushWithDelimiters(entry.member)
  const userLink = entry.userLink
  if (userLink && typeof userLink === "object") {
    pushCandidate(userLink.emailAddress)
    pushCandidate(userLink.identifier)
    if (typeof userLink.name === "string") candidates.push(userLink.name)
    const user = userLink.user
    if (user && typeof user === "object") {
      pushCandidate(user.emailAddress)
      pushCandidate(user.identifier)
      if (typeof user.name === "string") candidates.push(user.name)
    }
    const direct = Array.isArray(userLink.directRoles)
      ? userLink.directRoles
      : []
    if (!entry.directRoles && direct.length) {
      entry.directRoles = direct
    }
  }
  if (typeof entry.name === "string") candidates.push(entry.name)
  const principal = entry.principal
  if (principal && typeof principal === "object") {
    pushCandidate(principal.emailAddress)
    pushCandidate(principal.identifier)
  }
  return candidates
}

export const normalizeUserAccessAuditPayload = (payload) => {
  const entries = []
  const addEntries = (value, accessLevel = "property") => {
    if (!value) return
    if (Array.isArray(value)) {
      value.forEach((item) => addEntries(item, accessLevel))
      return
    }
    if (value && typeof value === "object") {
      if (Array.isArray(value.accessBindings)) {
        value.accessBindings.forEach((item) => addEntries(item, accessLevel))
        return
      }
      if (Array.isArray(value.userLinks)) {
        value.userLinks.forEach((item) => addEntries(item, accessLevel))
        return
      }
      entries.push({ entry: value, accessLevel })
    }
  }

  if (payload && typeof payload === "object") {
    if (payload.propertyAccessBindings)
      addEntries(payload.propertyAccessBindings, "property")
    if (payload.accountAccessBindings)
      addEntries(payload.accountAccessBindings, "account")
    if (payload.userAccessBindings) addEntries(payload.userAccessBindings, "property")
    if (payload.userAccessBinding) addEntries(payload.userAccessBinding, "property")
    if (payload.auditUserLinks) addEntries(payload.auditUserLinks, "property")
  } else {
    addEntries(payload, "property")
  }

  return entries
    .map(({ entry, accessLevel }, index) => {
      const directRolesRaw =
        entry?.directRoles ?? entry?.roles ?? entry?.userLink?.directRoles ?? []
      const effectiveRolesRaw =
        entry?.effectiveRoles ?? entry?.roles ?? entry?.userLink?.effectiveRoles ?? []
      const directRoles = toStringArray(directRolesRaw)
      const effectiveRoles = toStringArray(effectiveRolesRaw)
      const identifierCandidates = resolveIdentifierCandidates(entry)
      let identifier = identifierCandidates.find((candidate) => candidate)
      if (!identifier && identifierCandidates.length) {
        identifier = identifierCandidates[0]
      }
      const normalizedIdentifier = normalizeUserAccessIdentifier(identifier)
      const resolvedAccessLevel =
        typeof accessLevel === "string" && accessLevel ? accessLevel : "property"

      const principalEmail =
        typeof entry?.emailAddress === "string"
          ? entry.emailAddress.trim()
          : typeof entry?.userLink?.emailAddress === "string"
          ? entry.userLink.emailAddress.trim()
          : typeof entry?.principal?.emailAddress === "string"
          ? entry.principal.emailAddress.trim()
          : null
      const principalType = extractPrincipalType(entry)
      const fallbackUserLinkName =
        typeof entry?.userLink?.name === "string"
          ? entry.userLink.name
          : typeof entry?.name === "string"
          ? entry.name
          : null
      const userLinkName = normalizedIdentifier || fallbackUserLinkName

      const hasMeaningfulData =
        Boolean(identifier) ||
        Boolean(normalizedIdentifier) ||
        Boolean(principalEmail) ||
        Boolean(principalType) ||
        Boolean(userLinkName) ||
        directRoles.length > 0 ||
        effectiveRoles.length > 0

      if (!hasMeaningfulData) {
        const hasNonMetadataValues =
          entry &&
          typeof entry === "object" &&
          Object.keys(entry).some((key) => {
            if (key === "nextPageToken" || key === "totalSize") return false
            const value = entry[key]
            if (value === null || value === undefined) return false
            if (Array.isArray(value)) return value.length > 0
            if (typeof value === "object")
              return Object.keys(value).length > 0
            if (typeof value === "string") return value.trim().length > 0
            return true
          })
        if (!hasNonMetadataValues) {
          return null
        }
      }

      const fallbackKey =
        normalizedIdentifier ||
        normalizeUserAccessIdentifier(entry?.userLink?.name) ||
        normalizeUserAccessIdentifier(entry?.name) ||
        stableStringify(entry)

      const raw =
        entry && typeof entry === "object"
          ? { ...entry, __accessLevel: resolvedAccessLevel }
          : entry

      return {
        key: `${fallbackKey}-${index}`,
        identifier: identifier || null,
        normalizedIdentifier,
        principalEmail,
        principalType,
        userLinkName,
        directRoles,
        effectiveRoles,
        accessLevel: resolvedAccessLevel,
        raw,
      }
    })
    .filter(Boolean)
}

export const createUserAccessFingerprint = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return null
  const normalized = rows.map((row) => ({
    id:
      row.normalizedIdentifier ||
      normalizeUserAccessIdentifier(row.identifier) ||
      normalizeUserAccessIdentifier(row.userLinkName) ||
      row.key,
    directRoles: toStringArray(row.directRoles).slice().sort(),
    effectiveRoles: toStringArray(row.effectiveRoles).slice().sort(),
  }))
  normalized.sort((a, b) => {
    const aId = a.id || ""
    const bId = b.id || ""
    return aId.localeCompare(bId)
  })
  return stableStringify(normalized)
}

const normalizeTemplateExpectedUser = (entry) => {
  if (!entry || typeof entry !== "object") return null
  const identifier =
    typeof entry.identifier === "string"
      ? entry.identifier
      : typeof entry.email === "string"
      ? entry.email
      : typeof entry.address === "string"
      ? entry.address
      : ""
  const normalizedIdentifier = normalizeUserAccessIdentifier(identifier)
  if (!normalizedIdentifier) return null
  const label =
    typeof entry.label === "string"
      ? entry.label
      : typeof entry.name === "string"
      ? entry.name
      : ""
  const requiredRoles = toStringArray(entry.requiredRoles)
  const notes =
    typeof entry.notes === "string"
      ? entry.notes
      : typeof entry.description === "string"
      ? entry.description
      : ""
  return {
    identifier,
    normalizedIdentifier,
    label,
    requiredRoles,
    notes,
  }
}

export const extractTemplateExpectedUsers = (template) => {
  if (!template || typeof template !== "object") return []
  const rawList = Array.isArray(template.userAccess?.expectedUsers)
    ? template.userAccess.expectedUsers
    : Array.isArray(template.expectedUsers)
    ? template.expectedUsers
    : []
  return rawList.map(normalizeTemplateExpectedUser).filter(Boolean)
}

const buildIdentifierSet = (rows) => {
  const map = new Map()
  for (const row of rows || []) {
    if (!row) continue
    const normalized =
      row.normalizedIdentifier ||
      normalizeUserAccessIdentifier(row.identifier) ||
      normalizeUserAccessIdentifier(row.userLinkName) ||
      normalizeUserAccessIdentifier(row.principalEmail) ||
      normalizeUserAccessIdentifier(row.key)
    if (!normalized) continue
    if (!map.has(normalized)) map.set(normalized, row)
  }
  return map
}

export const evaluateUserAccessIssues = ({
  rows,
  status,
  confirmationIdentifiers,
  expectedUsers,
}) => {
  const normalizedRows = Array.isArray(rows) ? rows : []
  const currentMap = buildIdentifierSet(normalizedRows)
  const currentIdentifiers = new Set(currentMap.keys())
  const expectedList = Array.isArray(expectedUsers) ? expectedUsers : []
  const expectedMissing = expectedList.filter(
    (user) => user && !currentIdentifiers.has(user.normalizedIdentifier)
  )

  const confirmedSet = new Set(
    Array.isArray(confirmationIdentifiers)
      ? confirmationIdentifiers
          .map((value) => normalizeUserAccessIdentifier(value))
          .filter(Boolean)
      : []
  )

  const confirmationPending = confirmedSet.size === 0
  let newIdentifiers = []
  let removedIdentifiers = []
  if (confirmationPending) {
    newIdentifiers = Array.from(currentIdentifiers)
  } else {
    newIdentifiers = Array.from(currentIdentifiers).filter(
      (id) => !confirmedSet.has(id)
    )
    removedIdentifiers = Array.from(confirmedSet).filter(
      (id) => !currentIdentifiers.has(id)
    )
  }

  let issueCount = 0
  if (status && status !== "ok") {
    issueCount = 1
  } else {
    if (confirmationPending) {
      issueCount += currentIdentifiers.size
    } else {
      issueCount += newIdentifiers.length + removedIdentifiers.length
    }
  }
  issueCount += expectedMissing.length

  return {
    issueCount,
    expectedMissing,
    newIdentifiers: new Set(newIdentifiers),
    removedIdentifiers: new Set(removedIdentifiers),
    confirmationPending,
    currentIdentifiers,
  }
}
